import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { updateGBEori } from '../../../api/settings';
import { getEORIValidation } from '../../../api/util';

const SetEORINumberModal = ({ isOpen, setIsOpen, onContinue, company = {} }) => {
  const [companyDetails, setCompanyDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState({});

  const GENERIC_ERROR = 'An unknown error occurred. Please try again later.';

  useEffect(() => {
    if (company && isOpen) {
      setCompanyDetails({
        id: company?.companyId,
        name: company?.companyName,
        eori: company?.companyEori,
      });
    } else {
      setCompanyDetails({});
    }
  }, [company, isOpen]);

  useEffect(() => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError('');
    setValidation({});
  }, [isOpen]);

  useEffect(() => {
    validateFields();
  }, [companyDetails]);

  const validateFields = () => {
    const required = ['eori'];
    required.forEach((field) => {
      let validationError = '';
      const value = companyDetails[field];

      if (value?.trim()?.length === 0) {
        validationError = 'This field is required';
      }

      setValidation((prev) => ({
        ...prev,
        [field]: validationError,
      }));
    });
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    setCompanyDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setIsSubmitting(true);

      const eoriValidation = await getEORIValidation(companyDetails.eori, true);

      if (eoriValidation.statusDescr !== 'Valid') {
        setIsSubmitting(false);
        setValidation((prev) => ({
          ...prev,
          eori: 'Must be a valid GB EORI number.',
        }));
        return;
      }

      const response = await updateGBEori(companyDetails.id, companyDetails.eori);

      if (response.status === 0) {
        /** error */
        setIsSubmitting(false);
        setError(response.message || GENERIC_ERROR);
      } else {
        /** success */
        setIsSubmitting(false);
        setIsSuccess(true);
      }
    } catch (e) {
      setIsSubmitting(false);
      setError(e.error || GENERIC_ERROR);
    }
  };

  const handleOnClose = () => {
    if (!isSubmitting) {
      if (isSuccess) {
        // we should also run onContinue method if user closes modal after success
        onContinue();
      }
      setIsOpen(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleOnClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          p: 4,
          borderRadius: 5,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2">
          EORI Number Required
        </Typography>

        {isSuccess ? (
          <>
            <Alert sx={{ marginTop: 2, marginBottom: 4 }}>
              EORI number updated successfully. Please click below to continue.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span></span>
              <Button
                variant="contained"
                onClick={() => {
                  setIsOpen(false);
                  onContinue();
                }}
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingTop: 2, paddingBottom: 4 }}>
              <Typography variant="p" component="p">
                We require a GB EORI number for your company. Please confirm this below.
              </Typography>
            </Box>

            {error && (
              <Box sx={{ paddingBottom: 4 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <Box sx={{ paddingBottom: 4 }}>
              <TextField
                id="outlined-basic"
                label="GB EORI Number"
                variant="outlined"
                name="eori"
                value={companyDetails.eori}
                onChange={handleOnChange}
                disabled={isSubmitting}
                error={!!validation.eori}
                helperText={validation.eori}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>

              <Button
                variant="contained"
                loading={isSubmitting}
                onClick={handleSubmit}
                disabled={!!Object.keys(validation).some((field) => validation[field] !== '')}
              >
                Confirm
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SetEORINumberModal;
